import type { WinMethodCode } from "./match-format";
import type { BoutDecision } from "./ranking";

/**
 * BOUT — each combat sport's profile: how it decides a bout (`BOUT_DECISIONS`),
 * its default format, the win methods its operators pick from, the tallies
 * its scoresheet keeps and what its penalty count is called. One table, read
 * by OVR's combat screen AND the public results feed, so the score the portal
 * prints is the score the operator saw.
 *
 * Keyed by the SEAG `sports.code` (aliases for the older spellings). Values
 * follow the predecessor OVR (WSL-OVR-X, the SUKMA 2026 screens) except where
 * a line says it fixes one of its gaps; `CONFIRM` marks a number to check with
 * the sport's technical delegate.
 */

/** Tallies a slot may keep (`detail.tallies[].code`), with the English the screens and the portal print. */
export const BOUT_TALLY_LABELS = {
	head_kicks: "Head kicks",
	body_kicks: "Body kicks",
	punches: "Punches",
	turning_kicks: "Turning kicks",
	ippon: "Ippon",
	waza_ari: "Waza-ari",
	yuko: "Yuko",
	shido: "Shido",
	/** Muay: counts of the corner being knocked down (old OVR `kd_r{n}`, kept as the bout's total). */
	knockdowns: "Knockdowns",
} as const satisfies Record<string, string>;
export type BoutTallyCode = keyof typeof BOUT_TALLY_LABELS;
export const BOUT_TALLY_CODES = Object.keys(BOUT_TALLY_LABELS) as [BoutTallyCode, ...BoutTallyCode[]];

/** The printed name of a tally; an unknown code with its underscores as spaces. */
export function boutTallyLabel(code: string): string {
	return (BOUT_TALLY_LABELS as Readonly<Record<string, string>>)[code] ?? code.replace(/_/g, " ");
}

/** What a sport's per-round penalty count is called. */
export const BOUT_PENALTY_LABELS = {
	gamJeom: "Gam-jeom",
	warning: "Warnings",
	shido: "Shido",
	penalty: "Penalties",
} as const satisfies Record<string, string>;
export type BoutPenaltyKind = keyof typeof BOUT_PENALTY_LABELS;

/** A format: rounds (periods, relay legs), their length in seconds, and the score that ends the bout. */
export interface BoutFormatDefaults {
	rounds: number;
	roundSeconds?: number;
	target?: number;
}

export interface BoutProfile {
	decideBy: BoutDecision;
	/**
	 * Rounds when the meet's `config.rounds` is not set. Never silently 1: a
	 * one-round default would let the screen name a winner after round 1 of a
	 * three-round bout. Only sports fought as a single contest (judo, karate
	 * kumite) default to 1.
	 */
	defaultRounds: number;
	/** Round length in seconds when `config.round_seconds` is not set (display only). */
	defaultRoundSeconds?: number;
	/** The score that ends the bout when `config.target` is not set (fencing). */
	defaultTarget?: number;
	/** A pool (GROUP phase) bout's own defaults — fencing's 1 × 3:00 to 5. */
	pool?: BoutFormatDefaults;
	/** A TEAM event's own defaults — fencing's 9-leg relay to 45. */
	team?: BoutFormatDefaults;
	/** Bout win methods, picker order, most common first. */
	winMethods: readonly WinMethodCode[];
	/** How a ROUNDS sport records who won a round (`rounds[].win_by`); empty = the score alone. */
	roundMethods: readonly WinMethodCode[];
	/** Tallies offered even before any are stored. */
	tallies: readonly BoutTallyCode[];
	penalty: BoutPenaltyKind;
	/**
	 * Judges' seats the card offers when the meet has no JUDGE officials
	 * assigned (J1…Jn) — the old OVR's fixed panel. 0 = no card unless
	 * officials or stored cards exist.
	 */
	judges: number;
}

/**
 * Per sport. Sources and decisions:
 *   TKD  WT — best of 3 rounds of 2:00; a round's decision PTF / PTG / GDP / SUP (old OVR "Round Win").
 *        DQB added (old OVR). Penalties (gam-jeom) are counted, never subtracted (fixes the old OVR).
 *   BOX  World Boxing — 3 × 3:00, 5 judges' cards (old OVR's 5 × 3 table); WP RSC RSCI KO ABD DSQ WO.
 *   JUD  IJF — one contest of 4:00; the score ladder ippon > waza-ari > yuko > fewer shido (old OVR).
 *   KAR  WKF kumite — one bout of 3:00, points (old OVR: points per bout).
 *   MUA  IFMA — 3 × 3:00 (CONFIRM: SEA Games has used 3 × 2:00), 5 judges' cards — the judges'
 *        majority, as boxing (fixes the old OVR's "mode of the judges' round scores"); knockdowns tally.
 *   PSL  PERSILAT tanding — 3 × 2:00, the TOTAL of the rounds (old OVR; was JUDGES here). Methods
 *        PTS WT ABS RSC WDR DSQ WO (old WP → PTS, DQ → DSQ; WT / ABS CONFIRM).
 *   WSH  IWUF sanda — best of 3 rounds of 2:00 (old OVR: sets won); a level round goes on
 *        tie-break, recorded as the round's SUP (CONFIRM).
 *   FEN  FIE — TOTAL with a target: direct elimination 3 × 3:00 to 15, a pool bout 1 × 3:00 to 5,
 *        a team relay 9 legs to 45 (old OVR capped 15 / 45 and never stopped early; a pool bout
 *        to 5 was missing). A level score at time → PRI (priority).
 *   any other  TOTAL, 3 rounds — no winner until every round is scored.
 */
const PROFILES: Readonly<Record<string, BoutProfile>> = {
	TKD: {
		decideBy: "ROUNDS",
		defaultRounds: 3,
		defaultRoundSeconds: 120,
		winMethods: ["PTF", "PTG", "GDP", "SUP", "RSC", "WDR", "DSQ", "DQB", "PUN"],
		roundMethods: ["PTF", "PTG", "GDP", "SUP"],
		tallies: ["head_kicks", "body_kicks", "punches", "turning_kicks"],
		penalty: "gamJeom",
		judges: 0,
	},
	BOX: { decideBy: "JUDGES", defaultRounds: 3, defaultRoundSeconds: 180, winMethods: ["WP", "RSC", "RSCI", "KO", "ABD", "DSQ", "WO"], roundMethods: [], tallies: [], penalty: "warning", judges: 5 },
	JUD: {
		decideBy: "TALLIES",
		defaultRounds: 1,
		defaultRoundSeconds: 240,
		winMethods: ["IPPON", "WAZARI", "YUKO", "HSK", "FUS", "KIK"],
		roundMethods: [],
		tallies: ["ippon", "waza_ari", "yuko", "shido"],
		penalty: "shido",
		judges: 0,
	},
	KAR: { decideBy: "TOTAL", defaultRounds: 1, defaultRoundSeconds: 180, winMethods: ["PTS", "SENSHU", "HANTEI", "HSK", "KIK"], roundMethods: [], tallies: ["ippon", "waza_ari", "yuko"], penalty: "penalty", judges: 0 },
	MUA: {
		decideBy: "JUDGES",
		defaultRounds: 3,
		defaultRoundSeconds: 180,
		winMethods: ["WP", "KO", "RSCS", "RSCI", "RSCH", "RSCB", "WO", "DSQ"],
		roundMethods: [],
		tallies: ["knockdowns"],
		penalty: "warning",
		judges: 5,
	},
	PSL: { decideBy: "TOTAL", defaultRounds: 3, defaultRoundSeconds: 120, winMethods: ["PTS", "WT", "ABS", "RSC", "WDR", "DSQ", "WO"], roundMethods: [], tallies: [], penalty: "penalty", judges: 0 },
	WSH: { decideBy: "ROUNDS", defaultRounds: 3, defaultRoundSeconds: 120, winMethods: ["WPD", "WKO", "WIS", "WTV", "WDF", "DSQ"], roundMethods: ["SUP"], tallies: [], penalty: "warning", judges: 0 },
	FEN: {
		decideBy: "TOTAL",
		defaultRounds: 3,
		defaultRoundSeconds: 180,
		defaultTarget: 15,
		pool: { rounds: 1, roundSeconds: 180, target: 5 },
		team: { rounds: 9, roundSeconds: 180, target: 45 },
		winMethods: ["PTS", "PRI", "ABD", "DSQ", "WO"],
		roundMethods: [],
		tallies: [],
		penalty: "penalty",
		judges: 0,
	},
};
const ALIASES: Readonly<Record<string, string>> = {
	TAEKWONDO: "TKD",
	TKW: "TKD",
	BOXING: "BOX",
	JUDO: "JUD",
	KARATE: "KAR",
	KTE: "KAR",
	PCS: "PSL",
	SIL: "PSL",
	SILAT: "PSL",
	PSI: "PSL",
	MTI: "MUA",
	MUAY: "MUA",
	WUSHU: "WSH",
	WSS: "WSH",
	FENCING: "FEN",
};
const FALLBACK: BoutProfile = { decideBy: "TOTAL", defaultRounds: 3, winMethods: ["PTS", "PTF", "RSC", "KO", "WDR", "DSQ"], roundMethods: [], tallies: [], penalty: "penalty", judges: 0 };

/** A sport's bout profile, by `sports.code` (case-blind); the neutral fallback for a sport not described. */
export function boutProfileFor(sportCode: string | null | undefined): BoutProfile {
	const code = (sportCode ?? "").trim().toUpperCase();
	return PROFILES[ALIASES[code] ?? code] ?? FALLBACK;
}

/** Where the bout is fought: a pool (GROUP phase) bout, a TEAM event's match. */
export interface BoutContext {
	pool?: boolean;
	team?: boolean;
}

/**
 * Where a meet's bout is fought, from its event and phase: a TEAM event's
 * match (not a pair — 2 is doubles) and a pool (GROUP phase) bout. OVR's
 * screen and the public feed read it from here, never hand-written.
 */
export function boutContextOf(event: { competitor_type: string; team_size_max: number | null }, phaseType?: string | null): BoutContext {
	return { team: event.competitor_type === "TEAM" && event.team_size_max !== 2, pool: phaseType === "GROUP" };
}

/** The format a meet uses when its config says nothing: the team's, else the pool's, else the sport's. */
export function boutDefaults(profile: BoutProfile, ctx: BoutContext = {}): BoutFormatDefaults {
	const base: BoutFormatDefaults = { rounds: profile.defaultRounds };
	if (profile.defaultRoundSeconds !== undefined) base.roundSeconds = profile.defaultRoundSeconds;
	if (profile.defaultTarget !== undefined) base.target = profile.defaultTarget;
	if (ctx.team && profile.team) return { ...base, ...profile.team };
	if (ctx.pool && profile.pool) return { ...base, ...profile.pool };
	return base;
}
